import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import axiosInstance from '../../services/axiosInstance'
import { CheckCircle2, AlertCircle, Save, ArrowLeft } from 'lucide-react'
import PageHeader from '../../components/common/PageHeader'
import Autocomplete from 'react-google-autocomplete'
import { COUNTRIES } from '../../utils/countries'
import './styles.scss'

export default function CompleteProfile() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  const fromChecklist = location.state?.fromChecklist
  const [formData, setFormData] = useState({
    legalFirstName: user?.fullName?.split(' ')[0] || '',
    legalLastName: user?.fullName?.split(' ').slice(1).join(' ') || '',
    dateOfBirth: '',
    countryOfCitizenship: '',
    countryOfResidence: '',
    address: '',
    phoneCode: '+1',
    phoneNumber: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    // Try to load existing profile if any
    const fetchProfile = async () => {
      try {
        const res = await axiosInstance.get('/api/profiles/me')
        if (res.data?.profile) {
          const p = res.data.profile
          setFormData({
            legalFirstName: p.legalFirstName || formData.legalFirstName,
            legalLastName: p.legalLastName || formData.legalLastName,
            dateOfBirth: p.dateOfBirth ? new Date(p.dateOfBirth).toISOString().split('T')[0] : '',
            countryOfCitizenship: p.countryOfCitizenship || '',
            countryOfResidence: p.countryOfResidence || '',
            address: p.address || '',
            phoneCode: p.phone ? (p.phone.includes(' ') ? p.phone.split(' ')[0] : '+1') : '+1',
            phoneNumber: p.phone ? (p.phone.includes(' ') ? p.phone.substring(p.phone.indexOf(' ') + 1) : p.phone) : ''
          })
        }
      } catch (e) {
        // Ignored, might not have a profile yet
      }
    }
    fetchProfile()
  }, [])

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const submitData = { ...formData }
      delete submitData.phoneCode
      delete submitData.phoneNumber

      const res = await axiosInstance.put('/api/profiles/me', {
        ...submitData,
        phone: `${formData.phoneCode} ${formData.phoneNumber}`,
        dateOfBirth: new Date(formData.dateOfBirth).toISOString()
      })
      
      if (res.data.success) {
        setSuccess('Profile updated successfully! Redirecting...')
        
        if (fromChecklist || user?.teamId) {
          setTimeout(() => navigate('/dashboard'), 1500)
        } else {
          try {
            const appRes = await axiosInstance.get('/api/applications/me')
            if (appRes.data && appRes.data.application) {
              setTimeout(() => navigate('/dashboard'), 1500)
            } else {
              setTimeout(() => navigate('/discover'), 1500)
            }
          } catch (e) {
            setTimeout(() => navigate('/discover'), 1500)
          }
        }
      } else {
        setError(res.data.message || 'Failed to update profile.')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while saving your profile.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="complete-profile-page">
      {fromChecklist && (
        <div style={{ marginBottom: '1rem' }}>
          <button 
            onClick={() => navigate('/dashboard')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'none',
              border: 'none',
              color: '#64748b',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              padding: '0'
            }}
          >
            <ArrowLeft size={18} /> Back
          </button>
        </div>
      )}

      <PageHeader 
        title="Complete Your Profile" 
        subtitle="We need a few more details before you can start applying to universities."
      />

      <div className="profile-form-container glass-panel">
        {error && <div className="alert alert-danger"><AlertCircle size={18} /> {error}</div>}
        {success && <div className="alert alert-success"><CheckCircle2 size={18} /> {success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Legal First Name <span className="required">*</span></label>
              <input 
                type="text" 
                name="legalFirstName"
                value={formData.legalFirstName} 
                onChange={handleChange} 
                required 
                placeholder="As it appears on your passport"
              />
            </div>
            
            <div className="form-group">
              <label>Legal Last Name <span className="required">*</span></label>
              <input 
                type="text" 
                name="legalLastName"
                value={formData.legalLastName} 
                onChange={handleChange} 
                required 
                placeholder="As it appears on your passport"
              />
            </div>

            <div className="form-group">
              <label>Date of Birth <span className="required">*</span></label>
              <input 
                type="date" 
                name="dateOfBirth"
                value={formData.dateOfBirth} 
                onChange={handleChange} 
                required 
              />
            </div>

            <div className="form-group">
              <label>Country of Citizenship <span className="required">*</span></label>
              <select 
                name="countryOfCitizenship"
                value={formData.countryOfCitizenship} 
                onChange={handleChange} 
                required
              >
                <option value="">Select Country</option>
                {COUNTRIES.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.flag} {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Country of Residence <span className="required">*</span></label>
              <select 
                name="countryOfResidence"
                value={formData.countryOfResidence} 
                onChange={handleChange} 
                required
              >
                <option value="">Select Country</option>
                {COUNTRIES.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.flag} {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group full-width">
              <label>Permanent Home Address <span className="required">*</span></label>
              <Autocomplete
                apiKey={import.meta.env.VITE_GOOGLE_PLACES_API_KEY}
                options={{ types: ['address'] }}
                onPlaceSelected={(place) => {
                  setFormData(prev => ({ ...prev, address: place.formatted_address || place.name }));
                }}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                defaultValue={formData.address}
                placeholder="Search for your address..."
                required
              />
            </div>

            <div className="form-group">
              <label>Phone Number <span className="required">*</span></label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select 
                  name="phoneCode"
                  value={formData.phoneCode}
                  onChange={handleChange}
                  required
                  style={{ width: '130px', flexShrink: 0 }}
                >
                  {COUNTRIES.map((c) => (
                    <option key={`${c.name}-${c.code}`} value={c.code}>
                      {c.flag} {c.code}
                    </option>
                  ))}
                </select>
                <input 
                  type="tel" 
                  name="phoneNumber"
                  value={formData.phoneNumber} 
                  onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value.replace(/[^0-9]/g, '') }))} 
                  required 
                  placeholder="555 000 0000"
                  style={{ flex: 1 }}
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', height: '48px', fontSize: '1.05rem', marginTop: '1rem' }}>
              {loading ? 'Saving Profile...' : 'Save & Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
